import copyToClipboard from "./CopyToClipboard";
import pasteFromClipboard from "./PasteFromClipboard";
import {
  isActionPropertyCompatible,
  isActionPropertyCrossCompatible,
} from "./constants/PromptActionPropertyRules";

const CLIPBOARD_ENVELOPE_KEY = "__promptActionsClipboard";
const CLIPBOARD_ENVELOPE_VERSION = 1;

/**
 * Copies prompt actions to clipboard with optional property filtering and source-type context.
 *
 * When `sourceDeviceType` / `sourcePathType` are provided, the clipboard payload is wrapped in a
 * versioned envelope so paste can reason about the source context. Otherwise a flat object is
 * written for backward compatibility.
 *
 * @param {Object} actions - Object with action IDs as keys and action objects as values
 * @param {Object} [options={}]
 * @param {string} [options.sourceDeviceType] - Source prompt's device_type (stored in envelope)
 * @param {string} [options.sourcePathType] - Source prompt's path_type (stored in envelope)
 * @param {Array<string>} [options.includeProperties] - If specified, only these properties are copied. Takes precedence over excludeProperties.
 * @param {Array<string>} [options.excludeProperties] - Properties to exclude from copying (ignored if includeProperties is specified)
 * @param {string} [options.successMsg="Actions copied to clipboard."] - Custom success message
 * @param {string} [options.errorMsg="Couldn't copy actions to clipboard."] - Custom error message
 * @returns {Promise<void>}
 * @throws {Error} If actions object is empty or undefined
 */
export async function copyPromptActions(
  actions,
  {
    sourceDeviceType = null,
    sourcePathType = null,
    includeProperties = null,
    excludeProperties = [],
    successMsg = "Actions copied to clipboard.",
    errorMsg = "Couldn't copy actions to clipboard.",
  } = {},
) {
  if (!actions || typeof actions !== "object") {
    throw new Error("No actions to copy");
  }

  if (Object.keys(actions).length === 0) {
    throw new Error("No actions to copy");
  }

  const filterContext = createFilterContext(includeProperties, excludeProperties);
  const filtered = filterActionProperties(actions, filterContext);

  const payload =
    sourceDeviceType || sourcePathType
      ? {
          [CLIPBOARD_ENVELOPE_KEY]: CLIPBOARD_ENVELOPE_VERSION,
          sourceDeviceType,
          sourcePathType,
          actions: filtered,
        }
      : filtered;

  const jsonString = JSON.stringify(payload, null, 2);
  return copyToClipboard(jsonString, successMsg, errorMsg);
}

/**
 * Pastes prompt actions from clipboard and merges them with existing actions.
 * Updates actions with matching keys, preserves others.
 *
 * If `targetDeviceType` + `targetPathType` are provided and `strictTypeFilter` is true,
 * each action's properties are filtered via `isActionPropertyCompatible` so incompatible
 * properties from a different-type source are dropped.
 *
 * @param {Object} [existingActions={}] - Current actions object to merge with
 * @param {Object} [options={}]
 * @param {string} [options.targetDeviceType] - Target prompt's device_type (for type filtering)
 * @param {string} [options.targetPathType] - Target prompt's path_type (for type filtering)
 * @param {boolean} [options.strictTypeFilter=true] - If true and target types are provided, drop incompatible properties
 * @param {Array<string>} [options.includeProperties] - If specified, only these properties are kept from pasted actions
 * @param {Array<string>} [options.excludeProperties] - Properties to exclude from pasted actions (ignored if includeProperties is specified)
 * @param {boolean} [options.replaceAll=false] - If true, replaces all existing actions instead of merging
 * @returns {Promise<Object>} Merged actions object
 * @throws {Error} If clipboard content is not valid JSON or not an object
 */
export async function pastePromptActions(
  existingActions = {},
  {
    targetDeviceType = null,
    targetPathType = null,
    strictTypeFilter = true,
    includeProperties = null,
    excludeProperties = [],
    replaceAll = false,
  } = {},
) {
  const clipboardResult = await pasteFromClipboard();

  if (clipboardResult.type === "files") {
    throw new Error("Cannot paste files as prompt actions");
  }

  const clipboardData = clipboardResult.data;
  if (typeof clipboardData !== "string" || clipboardData.trim() === "") {
    throw new Error("Clipboard is empty or does not contain prompt actions");
  }

  let parsed;
  try {
    parsed = JSON.parse(clipboardData);
  } catch (error) {
    throw new Error("Clipboard content is not valid JSON for prompt actions");
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error("Clipboard content is not a valid actions object");
  }

  const isEnvelope = parsed[CLIPBOARD_ENVELOPE_KEY] === CLIPBOARD_ENVELOPE_VERSION;
  const pastedActions = isEnvelope ? parsed.actions : parsed;

  if (typeof pastedActions !== "object" || pastedActions === null || Array.isArray(pastedActions)) {
    throw new Error("Clipboard content is not a valid actions object");
  }

  const sourcePathType = isEnvelope ? parsed.sourcePathType : null;

  const filterContext = createFilterContext(includeProperties, excludeProperties);
  const shouldTypeFilter = strictTypeFilter && targetDeviceType && targetPathType;

  let filtered = filterActionProperties(pastedActions, filterContext);
  if (shouldTypeFilter) {
    filtered = filterByTypeCompatibility(
      filtered,
      targetDeviceType,
      targetPathType,
      sourcePathType,
    );
  }

  if (replaceAll) {
    return filtered;
  }

  // Don't let null/undefined values from the clipboard overwrite existing non-null ones.
  const merged = { ...existingActions };
  Object.entries(filtered).forEach(([key, value]) => {
    if (value != null) {
      merged[key] = value;
    } else if (!(key in existingActions)) {
      merged[key] = value;
    }
  });
  return merged;
}

function createFilterContext(includeProperties, excludeProperties) {
  return {
    includeProperties,
    excludeSet: excludeProperties.length > 0 ? new Set(excludeProperties) : null,
  };
}

function filterActionProperties(action, filterContext) {
  const { includeProperties, excludeSet } = filterContext;

  if (!includeProperties && !excludeSet) {
    return action;
  }

  const filtered = {};

  if (includeProperties) {
    includeProperties.forEach(prop => {
      if (prop in action) {
        filtered[prop] = action[prop];
      }
    });
  } else {
    Object.keys(action).forEach(key => {
      if (!excludeSet.has(key)) {
        filtered[key] = action[key];
      }
    });
  }

  return filtered;
}

function filterByTypeCompatibility(action, deviceType, pathType, sourcePathType) {
  const filtered = {};
  Object.keys(action).forEach(key => {
    if (
      isActionPropertyCompatible(key, deviceType, pathType) &&
      (sourcePathType == null || isActionPropertyCrossCompatible(key, sourcePathType, pathType))
    ) {
      filtered[key] = action[key];
    }
  });
  return filtered;
}
